import React, { useCallback, useEffect, useMemo, useState } from "react";

import styled from "styled-components";
import { Alert, Button, Card, Col, Row } from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";
import Selector from "../../components/selector";

import { FullWidthLoader } from "../../components/full-width-loader";
import { SectionHeading } from "../../components/section-heading";

import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { searchDiscrepancies } from "./services/discrepancies";

import CopyToClipboard from "react-copy-to-clipboard";
import { useConfig } from "../../lib/config";
import { useLocaleProvider } from "../../components/locale-provider";
import { AllowedLocales, getTranslatedCountryList } from "../../lib/intlUtils";

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    copyDataDiscrepancies: {
      id: "discrepancies.copyDataDiscrepancies",
      defaultMessage: "copyDataDiscrepancies",
    },
    copyBodyGHA: {
      id: "discrepancies.copyBodyGHA",
      defaultMessage: "copyBodyGHA",
    },
    copyResGHA: {
      id: "discrepancies.copyResGHA",
      defaultMessage: "copyResGHA",
    },
    copyBodyPropertyAri: {
      id: "discrepancies.copyBodyPropertyAri",
      defaultMessage: "copyBodyPropertyAri",
    },
    copyResPropertyAri: {
      id: "discrepancies.copyResPropertyAri",
      defaultMessage: "copyResPropertyAri",
    },
    copyBodyTripadvisor: {
      id: "discrepancies.copyBodyTripadvisor",
      defaultMessage: "copyBodyTripadvisor",
    },
    copyResTripadvisor: {
      id: "discrepancies.copyResTripadvisor",
      defaultMessage: "copyResTripadvisor",
    },
    copyBodyTrivago: {
      id: "discrepancies.copyBodyTrivago",
      defaultMessage: "copyBodyTrivago",
    },
    copyResTrivago: {
      id: "discrepancies.copyResTrivago",
      defaultMessage: "copyResTrivago",
    },
    copied: {
      id: "discrepancies.copied",
      defaultMessage: "Copied",
    },
    title: {
      id: "discrepancies.title",
      defaultMessage: "Target prices and verify discrepancies",
    },
    checkIn: {
      id: "discrepancies.checkIn",
      defaultMessage: "Check In",
    },
    checkOut: {
      id: "discrepancies.checkOut",
      defaultMessage: "CheckOut",
    },
    search: {
      id: "discrepancies.search",
      defaultMessage: "Search",
    },
    adults: {
      id: "discrepancies.adults",
      defaultMessage: "Adults",
    },
    kids: {
      id: "discrepancies.kids",
      defaultMessage: "Kids",
    },
    ages: {
      id: "discrepancies.ages",
      defaultMessage: "Ages",
    },
    device: {
      id: "discrepancies.device",
      defaultMessage: "Device",
    },
    currency: {
      id: "discrepancies.currency",
      defaultMessage: "Currency",
    },
    country: {
      id: "discrepancies.country",
      defaultMessage: "Country",
    },
    locale: {
      id: "discrepancies.locale",
      defaultMessage: "Locale",
    },
    propertyTickerSelectPlaceholder: {
      id: "channelManager.placeholder.propertyTicker",
      defaultMessage: "Engine identifier",
    },
  }),
};

const StyledTextArea = styled.textarea`
  height: 150px;
  margin: 16px;
`;

const StyledTextDiscrepancies = styled.div<{ error: boolean }>`
  font-size: 32px;
  color: ${({ error }) => (error ? "red" : "green")};
  text-align: center;
  margin: 16px auto;
`;

const CopyButton = styled(Button)`
  min-width: 85px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
`;

const ButtonContainerStart = styled.div`
  display: flex;
  margin: 1rem;
`;

const Label = styled.div`
  font-size: 18px;
  margin-bottom: -10px;
`;

const CardContainer = styled.div`
  max-width: 1024px;
`;

type Option = { value: string; label: string };

interface OperationError {
  message: string;
}

type DeviceInfoType = {
  osName: string;
  browserVersion: string;
  deviceType: string;
  browserName: string;
  browserEngineName: string;
};

type RunType = {
  trivago: boolean;
  tripadvisor: boolean;
  google: boolean;
};

type ConfigType = {
  propertyTicker: string;
  start: string;
  end: string;
  seniors: number;
  adults: number;
  teenagers: number;
  children: number;
  babies: number | null;
  guestAges: number[] | null;
  deviceInfo: DeviceInfoType;
  countryCode: string;
  cityName: string;
  currency: string;
};

const getDevice = () => {
  return [
    { value: "DESKTOP", label: "Desktop" },
    { value: "MOBILE", label: "Mobile" },
    { value: "TABLET", label: "Tablet" },
  ];
};

interface QuantityDemanded {
  value: number;
  label: number;
}

const getQuantityDemanded = (): QuantityDemanded[] => {
  const data: QuantityDemanded[] = [];
  const quantityDemanded = 12;
  for (let quantity = 0; quantity <= quantityDemanded; quantity++) {
    data.push({
      value: quantity,
      label: quantity,
    });
  }
  return data;
};

const enumCopy = {
  resDataDiscrepancies: "resDataDiscrepancies",
  bodyPropertyAri: "bodyPropertyAri",
  resPropertyAri: "resPropertyAri",
  bodyTrivago: "bodyTrivago",
  resTrivago: "resTrivago",
  bodyTripadvisor: "bodyTripadvisor",
  resTripadvisor: "resTripadvisor",
  bodyGHA: "bodyGHA",
  resGHA: "resGHA",
};

function ChannelManagerActivation() {
  const configEnv = useConfig();

  const userPropertyTickersList = useUserPropertyTickers();

  const [nameHasDiscrepancies, setNameHasDiscrepancies] = useState<[]>([]);

  const [resDataDiscrepancies, setResDataDiscrepancies] = useState<any>(null);

  const [bodyPropertyAri, setBodyPropertyAri] = useState<any>(null);
  const [resPropertyAri, setResPropertyAri] = useState<any>(null);

  const [bodyTrivago, setBodyTrivago] = useState<any>(null);
  const [resTrivago, setResTrivago] = useState<any>(null);

  const [bodyTripadvisor, setBodyTripadvisor] = useState<any>(null);
  const [resTripadvisor, setResTripadvisor] = useState<any>(null);

  const [bodyGHA, setBodyGHA] = useState<any>(null);
  const [resGHA, setResGHA] = useState<any>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [configSuccess] = useState<boolean>(false);

  const [propertyTickerOptions, setPropertyTickerOptions] = useState<Option[]>([]);
  const [propertyTickerOption, setPropertyTickerOption] = useState<Option | null | undefined>(
    undefined
  );

  const defQuantityDemanded = getQuantityDemanded()[0];
  const [valueCopied, setValueCopied] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [adults, setAdults] = useState(defQuantityDemanded);
  const [kids, setKids] = useState(defQuantityDemanded);
  const [agesKids, setAgesKids] = useState<any>([]);

  const [device, setDevice] = useState(getDevice()[0]);
  const [currency, setCurrency] = useState("EUR");
  const [country, setCountry] = useState({ label: "ES", value: "Es" });
  const [locale] = useState("Barcelona");

  const localeProvider = useLocaleProvider();
  const countryList = useMemo(() => {
    if (localeProvider?.currentLocale?.substring(0, 2)) {
      return getTranslatedCountryList(
        localeProvider?.currentLocale?.substring(0, 2) as AllowedLocales
      )
        .reverse()
        .reduce((accum, curr) => {
          return {
            [curr.value]: { label: curr.value, value: curr.value },
            ...accum,
          };
        }, []);
    }
  }, [localeProvider]);

  useEffect(() => {
    const fetchPropertyTickers = async () => {
      setLoading(true);
      setPropertyTickerOptions(
        userPropertyTickersList.map(option => ({ value: option, label: option }))
      );
      setLoading(false);
    };
    fetchPropertyTickers();
  }, [userPropertyTickersList]);

  useEffect(() => {
    const data: number[] = [];
    for (let index = 0; index < kids.value; index++) {
      data.push(0);
    }
    setAgesKids(data);
  }, [kids]);

  const handleSelectPropertyTicker = useCallback(
    selectedOption => setPropertyTickerOption(selectedOption),
    [setPropertyTickerOption]
  );

  const handleSelectCountry = useCallback(selectedOption => setCountry(selectedOption), [
    setCountry,
  ]);

  const validateForm = () =>
    propertyTickerOption?.value &&
    checkIn &&
    checkOut &&
    adults?.value &&
    country &&
    locale &&
    currency;

  const handleSearch = async () => {
    if (!validateForm()) {
      setError({ message: "Verificar campos" });
      return;
    }
    setLoading(true);
    setNameHasDiscrepancies([]);
    const config: ConfigType = {
      propertyTicker: propertyTickerOption ? propertyTickerOption.value : "",
      start: checkIn,
      end: checkOut,
      seniors: 0,
      adults: adults.value,
      teenagers: 0,
      children: kids.value,
      babies: null,
      guestAges: agesKids.length > 0 ? agesKids.map((item: any) => item.value) : null,
      deviceInfo: {
        osName: "Linux",
        browserVersion: "86.0.4240.193",
        deviceType: device.value,
        browserName: "Chrome",
        browserEngineName: "Blink",
      },
      countryCode: country?.label,
      cityName: locale,
      currency: currency,
    };

    const run: RunType = {
      trivago: true,
      tripadvisor: true,
      google: true,
    };

    const tolerance: number = 0.99;

    const urls = {
      URL_PROPERTY_ARI_FIRST: configEnv.URL_PROPERTY_ARI_FIRST,
      URL_PROPERTY_ARI_SECOND: configEnv.URL_PROPERTY_ARI_SECOND,
      URL_DISCREPANCIES_TRIVAGO: configEnv.URL_DISCREPANCIES_TRIVAGO,
      URL_DISCREPANCIES_TRIPADVISOR: configEnv.URL_DISCREPANCIES_TRIPADVISOR,
      URL_DISCREPANCIES_GOOGLE: configEnv.URL_DISCREPANCIES_GOOGLE,
    };

    const {
      initialResultJson: dataDiscrepancies,
      bodyGHA,
      bodyPropertyAri,
      bodyTripadvisor,
      bodyTrivago,
      resTrivago,
      resTripadvisor,
      restPropertyAri,
      resGHA: resGha
    } = await searchDiscrepancies(config, run, tolerance, nameHasDiscrepancies ,setNameHasDiscrepancies, urls);
    setResDataDiscrepancies(dataDiscrepancies);
    setBodyGHA(bodyGHA);
    setBodyPropertyAri(bodyPropertyAri);
    setBodyTripadvisor(bodyTripadvisor);
    setBodyTrivago(bodyTrivago);
    setResGHA(resGha);
    setResPropertyAri(restPropertyAri);
    setResTripadvisor(resTripadvisor);
    setResTrivago(resTrivago);
    setLoading(false);
  };

  if (loading) {
    return <FullWidthLoader />;
  }

  const changeAgeKid = (value: Option, index: number) => {
    const newAgesKids = [...agesKids];
    newAgesKids[index] = value;
    setAgesKids(newAgesKids);
  };

  const renderResAndBodyOf = ({
    body,
    enumCopyValueCopies,
    messageCopyBody,
    res,
    enumCopyRes,
    messageCopyRes,
  }: any) => {
    return (
      <>
        <ButtonContainer>
          {
            <CopyToClipboard
              text={JSON.stringify(body)}
              onCopy={() => {
                setValueCopied(enumCopyValueCopies);
              }}>
              <CopyButton variant={"success"} type="button" disabled={false}>
                {valueCopied === enumCopyValueCopies ? (
                  <FormattedMessage {...messages.copied} />
                ) : (
                  <FormattedMessage {...messageCopyBody} />
                )}
              </CopyButton>
            </CopyToClipboard>
          }
        </ButtonContainer>
        <StyledTextArea readOnly value={JSON.stringify(body)} />
        <ButtonContainer>
          {
            <CopyToClipboard
              text={JSON.stringify(res)}
              onCopy={() => {
                setValueCopied(enumCopyRes);
              }}>
              <CopyButton variant={"success"} type="button" disabled={false}>
                {valueCopied === enumCopyRes ? (
                  <FormattedMessage {...messages.copied} />
                ) : (
                  <FormattedMessage {...messageCopyRes} />
                )}
              </CopyButton>
            </CopyToClipboard>
          }
        </ButtonContainer>
        <StyledTextArea readOnly value={JSON.stringify(res)} />
      </>
    );
  };
  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <CardContainer>
        <Card className="ml-4 mr-5 mb-4 mt-4">
          <Container>
            <Label>
              <FormattedMessage {...messages.propertyTickerSelectPlaceholder} />
            </Label>
            <Selector
              options={propertyTickerOptions}
              value={propertyTickerOption}
              onChange={handleSelectPropertyTicker}
              placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
            />

            <Label>
              <FormattedMessage {...messages.checkIn} />
            </Label>
            <input
              type="date"
              id="checkIn"
              name="checkIn"
              value={checkIn}
              onChange={event => {
                setCheckIn(event.target.value);
              }}
            />

            <Label>
              <FormattedMessage {...messages.checkOut} />
            </Label>
            <input
              type="date"
              id="checkOut"
              name="checkOut"
              value={checkOut}
              onChange={event => {
                setCheckOut(event.target.value);
              }}
            />

            <Label>
              <FormattedMessage {...messages.adults} />
            </Label>
            <Selector
              options={getQuantityDemanded()}
              value={adults}
              onChange={setAdults}
              placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
            />

            <Label>
              <FormattedMessage {...messages.kids} />
            </Label>
            <Selector
              options={getQuantityDemanded()}
              value={kids}
              onChange={setKids}
              placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
            />

            {agesKids.length > 0 && (
              <Row>
                {agesKids.map((agekid: number, index: number) => {
                  return (
                    <Col xs={4}>
                      <Selector
                        options={getQuantityDemanded()}
                        value={agekid}
                        onChange={(option: any) => {
                          changeAgeKid(option, index);
                        }}
                        placeholder={<FormattedMessage {...messages.ages} />}
                      />
                    </Col>
                  );
                })}
              </Row>
            )}

            <Label>
              <FormattedMessage {...messages.device} />
            </Label>
            <Selector
              options={getDevice()}
              value={device}
              onChange={setDevice}
              placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
            />

            <Label>
              <FormattedMessage {...messages.currency} />
            </Label>
            <input
              id="currency"
              name="currency"
              onChange={event => {
                setCurrency(event.target.value);
              }}
              value={currency}
            />

            <Label>
              <FormattedMessage {...messages.country} />
            </Label>
            <Selector
              options={Object.values({ ...countryList })}
              value={country}
              onChange={handleSelectCountry}
              placeholder={<FormattedMessage {...messages.country} />}
            />
          </Container>
          {error && (
            <Alert variant="danger">
              <strong>
                <FormattedMessage {...messages.saveError} />
              </strong>
            </Alert>
          )}
          {configSuccess && (
            <Alert variant="success">
              <strong>
                <FormattedMessage {...messages.saveSuccess} />
              </strong>
            </Alert>
          )}
          <ButtonContainerStart>
            <Button variant="primary" type="button" onClick={handleSearch}>
              <FormattedMessage {...messages.search} />
            </Button>
          </ButtonContainerStart>

          {bodyPropertyAri && (
            <StyledTextDiscrepancies error={nameHasDiscrepancies.length > 0 ? true : false}>
              {nameHasDiscrepancies.length > 0 ? `HAY DISCREPANCIAS. ${nameHasDiscrepancies.join(', ')}` : "NO HAY DISCREPANCIAS"}
            </StyledTextDiscrepancies>
          )}

          {resPropertyAri &&
            renderResAndBodyOf({
              body: bodyPropertyAri,
              enumCopyValueCopies: enumCopy.bodyPropertyAri,
              messageCopyBody: messages.copyBodyPropertyAri,
              res: resPropertyAri,
              enumCopyRes: enumCopy.resPropertyAri,
              messageCopyRes: messages.copyResPropertyAri,
            })}
          {bodyGHA &&
            renderResAndBodyOf({
              body: bodyGHA,
              enumCopyValueCopies: enumCopy.bodyGHA,
              messageCopyBody: messages.copyBodyGHA,
              res: resGHA,
              enumCopyRes: enumCopy.resGHA,
              messageCopyRes: messages.copyResGHA,
            })}

          {bodyTripadvisor &&
            renderResAndBodyOf({
              body: bodyTripadvisor,
              enumCopyValueCopies: enumCopy.bodyTripadvisor,
              messageCopyBody: messages.copyBodyTripadvisor,
              res: resTripadvisor,
              enumCopyRes: enumCopy.resTripadvisor,
              messageCopyRes: messages.copyResTripadvisor,
            })}

          {bodyTrivago &&
            renderResAndBodyOf({
              body: bodyTrivago,
              enumCopyValueCopies: enumCopy.bodyTrivago,
              messageCopyBody: messages.copyBodyTrivago,
              res: resTrivago,
              enumCopyRes: enumCopy.resTrivago,
              messageCopyRes: messages.copyResTrivago,
            })}

          {bodyPropertyAri && (
            <>
              <ButtonContainer>
                {
                  <CopyToClipboard
                    text={JSON.stringify(resDataDiscrepancies)}
                    onCopy={() => {
                      setValueCopied(enumCopy.resDataDiscrepancies);
                    }}>
                    <CopyButton variant={"success"} type="button" disabled={false}>
                      {valueCopied === enumCopy.resDataDiscrepancies ? (
                        <FormattedMessage {...messages.copied} />
                      ) : (
                        <FormattedMessage {...messages.copyDataDiscrepancies} />
                      )}
                    </CopyButton>
                  </CopyToClipboard>
                }
              </ButtonContainer>
              <StyledTextArea readOnly value={JSON.stringify(resDataDiscrepancies)} />
            </>
          )}
        </Card>
      </CardContainer>
    </>
  );
}

export default withAuthorizationVerifier(ChannelManagerActivation);
